import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  SortDescriptor,
  State,
} from '@progress/kendo-data-query';
import { Catalog } from 'src/types/catalog';
import { CatalogTreeViewItem } from 'src/types/catalog/Catalog';
import { IFilters } from 'src/types/PostBody';
import { SearchProductsRequest } from 'src/types/search_products/SearchProducts';
import { modifyRequest } from 'src/utils/requestInterceptor';
import {
  mapProductsToFavoriteItems,
  mapSelectionsToCartItems,
} from 'src/utils/productActionMappers';
import { PRESERVE_SORT_FIELDS } from 'src/sections/catalog_products/ViewCatalogProductsContext';
import {
  SearchProductSelectionState,
  initialSearchListViewDataState,
  initialSearchPictureViewDataState,
} from './searchProductContext.types';
import { SearchCatalogOption, SearchResultProduct } from './searchProductData';

export const SEARCH_PRESERVE_SORT_FIELDS = [
  ...PRESERVE_SORT_FIELDS,
  'brandName',
  'uom',
  'catalogName',
];

export { mapProductsToFavoriteItems, mapSelectionsToCartItems };

export const DEFAULT_SEARCH_CATALOG_OPTION: SearchCatalogOption = {
  catalogId: 'all',
  catalogName: 'All Catalogs',
};

export const SEARCH_GRID_STRING_FIELDS = [
  'itemNum',
  'brandName',
  'description',
  'catalogName',
  'supplierName',
  'uom',
  'casePack',
  'manufacturerNumber',
  'weight',
];

export const buildSearchCatalogOptions = (catalogs?: Catalog[]): SearchCatalogOption[] => [
  DEFAULT_SEARCH_CATALOG_OPTION,
  ...(catalogs ?? []).map(({ catalogId, catalogName }) => ({ catalogId, catalogName })),
];

export const catalogTreeItemToSearchOption = (
  item: CatalogTreeViewItem
): SearchCatalogOption => ({
  catalogId: item.catalogId ?? 'all',
  catalogName: item.catalogName ?? item.text,
});

export const REFINE_SEARCH_MIN_LENGTH = 3;

export const hasRefineSearchInputError = (value: string): boolean => {
  const trimmed = value.trim();
  return trimmed.length > 0 && trimmed.length < REFINE_SEARCH_MIN_LENGTH;
};

export const getEffectiveRefineSearch = (value: string): string => {
  const trimmed = value.trim();
  return trimmed.length >= REFINE_SEARCH_MIN_LENGTH ? trimmed : '';
};

export interface PrimarySearchPendingParams {
  trimmedSearchTerm: string;
  settledSearchTerm: string;
  isRefineSearchActive: boolean;
  hasActiveFilters: boolean;
  isPendingSearchProducts: boolean;
  loadingSearchProducts: boolean;
  isFetchingSearchProducts: boolean;
  searchProductsResponse: unknown;
}

export const getIsPrimarySearchPending = ({
  trimmedSearchTerm,
  settledSearchTerm,
  isRefineSearchActive,
  hasActiveFilters,
  isPendingSearchProducts,
  loadingSearchProducts,
  isFetchingSearchProducts,
  searchProductsResponse,
}: PrimarySearchPendingParams): boolean => {
  if (!trimmedSearchTerm || isRefineSearchActive || hasActiveFilters) {
    return false;
  }

  const isUnsettledSearchTerm = settledSearchTerm !== trimmedSearchTerm;

  return (
    isPendingSearchProducts ||
    (loadingSearchProducts && searchProductsResponse === undefined) ||
    (isFetchingSearchProducts && isUnsettledSearchTerm)
  );
};

export const getShowSearchResultsContent = (
  hasNoPrimarySearchResults: boolean,
  isPrimarySearchPending: boolean
): boolean => !hasNoPrimarySearchResults && !isPrimarySearchPending;

export const buildPriceRangeFilters = (range: string): IFilters[] => {
  switch (range) {
    case 'under25':
      return [{ field: 'priceRange', value: '-:25', operator: 'eq' }];
    case '25to50':
      return [{ field: 'priceRange', value: '25:50', operator: 'eq' }];
    case '50to100':
      return [{ field: 'priceRange', value: '50:100', operator: 'eq' }];
    case '100to200':
      return [{ field: 'priceRange', value: '100:200', operator: 'eq' }];
    case 'over200':
      return [{ field: 'priceRange', value: '200:+', operator: 'eq' }];
    default:
      return [];
  }
};

export const buildToolbarFilters = ({
  catalogId,
  itemFilterValue,
  priceRangeFilter,
}: {
  catalogId: number | 'all';
  itemFilterValue: number;
  priceRangeFilter: string;
}): IFilters[] => {
  const extraFilters: IFilters[] = [];

  if (catalogId !== 'all') {
    extraFilters.push({
      field: 'catalogId',
      value: String(catalogId),
      operator: 'eq',
    });
  }

  if (itemFilterValue === 1) {
    extraFilters.push({
      field: 'isContracted',
      value: 'true',
      operator: 'eq',
    });
  }

  extraFilters.push(...buildPriceRangeFilters(priceRangeFilter));

  return extraFilters;
};

export const buildSearchProductsRequest = ({
  dataState,
  trimmedSearchTerm,
  effectiveRefineSearch,
  toolbarFilters,
}: {
  dataState: State;
  trimmedSearchTerm: string;
  effectiveRefineSearch: string;
  toolbarFilters: IFilters[];
}): SearchProductsRequest => {
  const queryOptions = modifyRequest(dataState, undefined, undefined, SEARCH_GRID_STRING_FIELDS);

  return {
    searchText: trimmedSearchTerm,
    refineText: effectiveRefineSearch || undefined,
    queryOptions: {
      clientSide: false,
      skip: queryOptions.skip,
      take: queryOptions.take,
      skipCalculatingTotalRecordsCount: false,
      sort: queryOptions.sort as SortDescriptor[],
      filters: [...queryOptions.filters, ...toolbarFilters],
    },
  };
};

const isCompositeFilter = (
  filter: CompositeFilterDescriptor | FilterDescriptor | null | undefined
): filter is CompositeFilterDescriptor =>
  filter != null && 'filters' in filter && Array.isArray(filter.filters);

export const getAttributesFilterKey = (
  filter: CompositeFilterDescriptor | undefined
): string => {
  if (!filter?.filters?.length) {
    return '';
  }

  for (const filterItem of filter.filters) {
    if (!filterItem) {
      continue;
    }

    if (isCompositeFilter(filterItem)) {
      const nestedKey = getAttributesFilterKey(filterItem);
      if (nestedKey) {
        return nestedKey;
      }
      continue;
    }

    if (filterItem.field !== 'attributes') {
      continue;
    }

    const value = String(filterItem.value ?? '').trim();
    if (!value) {
      return '';
    }

    const operator = filterItem.operator === 'and' ? 'and' : 'or';
    return `${operator}:${value}`;
  }

  return '';
};

export const EMPTY_GRID_FILTER: CompositeFilterDescriptor = { logic: 'and', filters: [] };

export const shouldResetItemFilterForAttributes = (
  previousKey: string,
  nextKey: string
): boolean => Boolean(nextKey) && nextKey !== previousKey;

export const shouldClearAttributesForContractedItems = (
  itemFilterValue: number,
  attributesFilterKey: string
): boolean => itemFilterValue === 1 && Boolean(attributesFilterKey);

export const removeFieldFromFilter = (
  filter: CompositeFilterDescriptor | undefined,
  field: string
): CompositeFilterDescriptor | undefined => {
  if (!filter?.filters?.length) {
    return undefined;
  }

  const filters = filter.filters
    .map((filterItem) => {
      if (!filterItem) {
        return null;
      }

      if (isCompositeFilter(filterItem)) {
        return removeFieldFromFilter(filterItem, field) ?? null;
      }

      if (filterItem.field === field) {
        return null;
      }

      return filterItem;
    })
    .filter((filterItem): filterItem is CompositeFilterDescriptor | FilterDescriptor =>
      filterItem != null
    );

  if (filters.length === 0) {
    return undefined;
  }

  return { ...filter, filters };
};

export const shouldPreserveSearchSort = (currentSortArray: SortDescriptor[]): boolean => {
  const fields = currentSortArray.map((sort) => sort.field).join('');
  return new RegExp(`(${SEARCH_PRESERVE_SORT_FIELDS.join('|')})`, 'gi').test(fields);
};

export const resolveSearchSortOnViewChange = (
  currentSort: SortDescriptor[] | undefined,
  targetView: number
): SortDescriptor[] | undefined => {
  const sort = currentSort ?? [];
  const defaultSort =
    targetView === 0
      ? initialSearchListViewDataState.sort
      : initialSearchPictureViewDataState.sort;

  if (!shouldPreserveSearchSort(sort)) {
    return defaultSort;
  }

  return sort.filter((descriptor) => SEARCH_PRESERVE_SORT_FIELDS.includes(descriptor.field));
};

export const hasNonDefaultSearchSort = (
  currentSort: SortDescriptor[] | undefined,
  defaultSort: SortDescriptor[] | undefined
): boolean => {
  const current = currentSort?.[0];
  const defaultDescriptor = defaultSort?.[0];

  return (
    current !== undefined &&
    (current.field !== defaultDescriptor?.field || current.dir !== defaultDescriptor?.dir)
  );
};

export const getHasActiveFilters = ({
  refineSearch,
  priceRangeFilter,
  itemFilterValue,
  catalogId,
  gridFilterCount,
  sort,
  defaultSort,
}: {
  refineSearch: string;
  priceRangeFilter: string;
  itemFilterValue: number;
  catalogId: number | 'all';
  gridFilterCount: number;
  sort?: SortDescriptor[];
  defaultSort?: SortDescriptor[];
}): boolean =>
  Boolean(refineSearch.trim()) ||
  Boolean(priceRangeFilter) ||
  itemFilterValue !== 0 ||
  catalogId !== 'all' ||
  Boolean(gridFilterCount) ||
  hasNonDefaultSearchSort(sort, defaultSort);

export const getHasNoPrimarySearchResults = ({
  trimmedSearchTerm,
  isRefineSearchActive,
  hasActiveFilters,
  isPrimarySearchPending,
  productsTotal,
}: {
  trimmedSearchTerm: string;
  isRefineSearchActive: boolean;
  hasActiveFilters: boolean;
  isPrimarySearchPending: boolean;
  productsTotal: number;
}): boolean =>
  Boolean(trimmedSearchTerm) &&
  !isRefineSearchActive &&
  !hasActiveFilters &&
  !isPrimarySearchPending &&
  productsTotal === 0;

export const getLoadingProductsCombined = ({
  loadingSearchProducts,
  isFetchingSearchProducts,
  productsLength,
  trimmedSearchTerm,
  isPendingSearchProducts,
  isPrimarySearchPending,
}: {
  loadingSearchProducts: boolean;
  isFetchingSearchProducts: boolean;
  productsLength: number;
  trimmedSearchTerm: string;
  isPendingSearchProducts: boolean;
  isPrimarySearchPending: boolean;
}): boolean =>
  loadingSearchProducts ||
  isFetchingSearchProducts ||
  (productsLength === 0 &&
    Boolean(trimmedSearchTerm) &&
    (isPendingSearchProducts || isPrimarySearchPending));

export const getHasSelectedItems = (
  productSelections: Record<string, SearchProductSelectionState>
): boolean => Object.values(productSelections).some((selection) => selection.selected);

export const getIsDirty = (
  productSelections: Record<string, SearchProductSelectionState>
): boolean =>
  Object.values(productSelections).some((selection) => selection.selected || selection.qty !== 0);

export const getProductSelectionValue = (
  productSelections: Record<string, SearchProductSelectionState>,
  productKey: string
): SearchProductSelectionState => productSelections[productKey] || { selected: false, qty: 0 };

export const getSelectedProductKeys = (
  productSelections: Record<string, SearchProductSelectionState>
): string[] =>
  Object.entries(productSelections)
    .filter(([, selection]) => selection.selected)
    .map(([productKey]) => productKey);

export const getShouldShowUnsavedModal = ({
  productSelections,
  products,
  skip,
  take,
}: {
  productSelections: Record<string, SearchProductSelectionState>;
  products: SearchResultProduct[];
  skip: number | undefined;
  take: number | undefined;
}): boolean => {
  const selectedItems = getSelectedProductKeys(productSelections);
  if (selectedItems.length === 0) {
    return false;
  }

  const selectedIndexes = products.reduce((indexes: number[], product, index) => {
    if (selectedItems.includes(product.productKey)) {
      indexes.push(index);
    }
    return indexes;
  }, []);

  const isFirstPage = skip === 0 || skip === undefined;
  const isWithinRange = take === 10 || take === 25;
  const isSelectedItemsIndexWithinRange = selectedIndexes.every((index) => index <= 24);

  if (isFirstPage && isWithinRange) {
    return false;
  }
  if (isFirstPage && !isWithinRange && isSelectedItemsIndexWithinRange) {
    return false;
  }

  return true;
};
